import { Component, Inject, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { catchError, filter, of, throwError, timeout } from 'rxjs';
import { AuthService } from '@admin-core/services/auth.service';
import { AdminChatSessionService } from '@admin-core/services/admin-chat-session.service';
import { ChatRealtimeService } from '@admin-core/services/chat-realtime.service';
import {
  MODULE_AI_MARKETING,
  MODULE_LIVE_CHAT,
  ModulesService
} from '@admin-core/services/modules.service';
import { UserRole } from '@admin-core/models/auth.model';
import { TranslationKey } from '@admin-core/i18n/translations/en';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { LanguageSwitcherComponent } from '@admin-shared/components/language-switcher/language-switcher.component';
import { AdminChatNotifierComponent } from '@admin-shared/components/admin-chat-notifier/admin-chat-notifier.component';
import { AiMarketingAssistantWidgetComponent } from '@ecommerce/admin/features/ai-marketing-admin/ai-marketing-assistant-widget.component';
import {
  ADMIN_SHELL_BRANDING,
  AdminShellBranding
} from '../tokens/admin-shell-branding.token';
import { adminNavIcon } from './admin-nav-icons';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    LanguageSwitcherComponent,
    AdminChatNotifierComponent,
    AiMarketingAssistantWidgetComponent
  ],
  templateUrl: './admin-layout.component.html',
  styleUrl: './admin-layout.component.scss'
})
export class AdminLayoutComponent {
  readonly appName;
  readonly logoUrl;
  readonly user;
  readonly businessKey;
  readonly businesses;
  readonly showBusinessSwitch;
  readonly tenantLabel;
  readonly outletEpoch = signal(0);
  readonly sessionReady;
  readonly unreadCount;
  readonly showChat;
  readonly showAiAssistant;
  readonly sidebarOpen = signal(false);
  readonly navIcon = adminNavIcon;
  readonly nav;
  readonly pageTitle;

  private readonly url = signal('');
  private bootstrapTask: Promise<void> | null = null;

  constructor(
    readonly auth: AuthService,
    private readonly router: Router,
    private readonly i18n: LanguageService,
    private readonly chatSession: AdminChatSessionService,
    private readonly chatRealtime: ChatRealtimeService,
    private readonly modules: ModulesService,
    @Inject(ADMIN_SHELL_BRANDING) private readonly branding: AdminShellBranding
  ) {
    this.appName = this.branding.appName;
    this.logoUrl = this.branding.logoUrl;
    this.user = this.auth.user;
    this.businessKey = this.auth.businessKey;
    this.businesses = computed(() => this.user()?.businesses ?? []);
    this.showBusinessSwitch = computed(
      () => this.auth.canSwitchBusiness() && this.businesses().length > 1
    );
    this.tenantLabel = computed(() => {
      const list = this.businesses();
      const key = this.businessKey();
      const selected = list.find((b) => b.key === key) ?? list[0];
      return selected?.name?.trim() || this.appName;
    });
    this.sessionReady = this.auth.adminSessionReady;
    this.unreadCount = this.chatRealtime.unreadCount;
    this.showChat = computed(
      () => this.modules.isEnabled(MODULE_LIVE_CHAT) && this.auth.canUseChat()
    );
    this.showAiAssistant = computed(() => {
      if (!this.modules.isEnabled(MODULE_AI_MARKETING)) {
        return false;
      }
      const path = this.url().split('?')[0];
      return path === '/admin/ai-marketing' || path.startsWith('/admin/ai-marketing/');
    });
    this.nav = computed(() => {
      const items: { path: string; labelKey: TranslationKey; exact: boolean }[] = [
        { path: '/admin', labelKey: 'admin.nav.dashboard', exact: true },
        { path: '/admin/orders', labelKey: 'admin.nav.orders', exact: false },
        { path: '/admin/products', labelKey: 'admin.nav.products', exact: false },
        { path: '/admin/bundles', labelKey: 'admin.nav.bundles', exact: false },
        { path: '/admin/brands', labelKey: 'admin.nav.brands', exact: false },
        { path: '/admin/categories', labelKey: 'admin.nav.categories', exact: false },
        { path: '/admin/category-filters', labelKey: 'admin.nav.categoryFilters', exact: false },
        { path: '/admin/category-specs', labelKey: 'admin.nav.categorySpecs', exact: false }
      ];

      if (this.auth.canManageUsers()) {
        items.push({
          path: '/admin/users',
          labelKey: 'admin.nav.users',
          exact: false
        });
      }

      if (this.auth.canManageModules()) {
        items.push({
          path: '/admin/settings',
          labelKey: 'admin.nav.settings',
          exact: false
        });
      }

      if (this.modules.isEnabled(MODULE_AI_MARKETING)) {
        items.push({
          path: '/admin/ai-marketing',
          labelKey: 'admin.nav.aiMarketing',
          exact: false
        });
      }

      if (this.showChat()) {
        items.push({
          path: '/admin/chat',
          labelKey: 'admin.nav.chat',
          exact: false
        });
      }

      const [dashboard, ...rest] = items;
      const locale = this.i18n.lang();
      rest.sort((a, b) =>
        this.i18n.t(a.labelKey).localeCompare(this.i18n.t(b.labelKey), locale, { sensitivity: 'base' })
      );
      return [dashboard, ...rest];
    });
    this.pageTitle = computed(() => {
      this.i18n.lang();
      const path = this.url().split('?')[0];
      const match = this.nav()
        .filter((item) =>
          item.exact ? path === item.path : path === item.path || path.startsWith(`${item.path}/`)
        )
        .sort((a, b) => b.path.length - a.path.length)[0];
      return this.i18n.t(match?.labelKey ?? 'admin.nav.dashboard');
    });

    this.url.set(this.router.url);

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.url.set((event as NavigationEnd).urlAfterRedirects);
      this.sidebarOpen.set(false);
    });

    if (this.auth.isLoggedIn() && this.auth.user()) {
      this.runBootstrap();
    }

    this.auth
      .refreshMe()
      .pipe(
        timeout({ first: 15000 }),
        catchError((err) => {
          const cached = this.auth.user();
          if (cached && this.auth.isLoggedIn()) {
            return of(cached);
          }

          return throwError(() => err);
        })
      )
      .subscribe({
        next: () => {
          this.runBootstrap();
        },
        error: () => {
          void this.chatSession.stop();
          this.auth.logout();
          void this.router.navigateByUrl('/login');
        }
      });
  }

  private runBootstrap(): void {
    if (this.bootstrapTask) {
      return;
    }

    this.bootstrapTask = this.bootstrapAdminSession().finally(() => {
      this.bootstrapTask = null;
    });
  }

  private async bootstrapAdminSession(): Promise<void> {
    await this.modules.load();
    this.outletEpoch.update((n: number) => n + 1);
    this.auth.markAdminSessionReady();
    if (this.showChat()) {
      void this.chatSession.refresh();
    }
  }

  onBusinessKeyChange(key: string): void {
    if (!key || key === this.auth.businessKey()) {
      return;
    }

    this.auth.setBusinessKey(key);
    this.auth.invalidateAdminSession();
    this.bootstrapTask = null;
    this.outletEpoch.update((n: number) => n + 1);

    if (this.router.url.startsWith('/admin/chat')) {
      void this.router.navigateByUrl('/admin/chat');
    }

    this.auth
      .refreshMe()
      .pipe(
        timeout({ first: 15000 }),
        catchError((err) => {
          const cached = this.auth.user();
          if (cached && this.auth.isLoggedIn()) {
            return of(cached);
          }

          return throwError(() => err);
        })
      )
      .subscribe({
        next: () => {
          this.runBootstrap();
        },
        error: () => {
          void this.chatSession.stop();
          this.auth.logout();
          void this.router.navigateByUrl('/login');
        }
      });
  }

  toggleSidebar(): void {
    this.sidebarOpen.update((open) => !open);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  logout(): void {
    void this.chatSession.stop();
    this.auth.logout();
    void this.router.navigateByUrl('/login');
  }

  exitImpersonation(): void {
    this.auth.exitImpersonation().subscribe({
      next: () => {
        this.outletEpoch.update((n: number) => n + 1);
        void this.chatSession.refresh();
        void this.router.navigateByUrl('/admin/users');
      },
      error: () => {
        void this.chatSession.stop();
        this.auth.logout();
        void this.router.navigateByUrl('/login');
      }
    });
  }

  roleKey(role: UserRole): TranslationKey {
    if (role === 'BusinessAdmin') {
      return 'users.role.BusinessAdmin';
    }
    return 'users.role.PortalAdmin';
  }
}
